import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Card, CardHeader, CardContent, Divider, Box, Typography, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import { customerApi } from '../../../api/customerApi';

const scoreColor = (score) => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#3b82f6';
  if (score >= 40) return '#f59e0b';
  if (score >= 20) return '#f97316';
  return '#ef4444';
};

const flagStyle = (key) => {
  switch (key) {
    case 'tendue':
    case 'injoignable':
      return { bg: 'rgba(239,68,68,0.1)', fg: '#dc2626', bd: 'rgba(239,68,68,0.3)' };
    case 'noshow':
      return { bg: 'rgba(249,115,22,0.1)', fg: '#ea580c', bd: 'rgba(249,115,22,0.3)' };
    default:
      return { bg: 'rgba(100,116,139,0.1)', fg: '#475569', bd: 'rgba(100,116,139,0.3)' };
  }
};

const Gauge = ({ score, color }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, (Number(score) || 0) / 100));
  return (
    <svg width="130" height="130" viewBox="0 0 130 130" style={{ flexShrink: 0 }}>
      <circle cx="65" cy="65" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
      <circle
        cx="65"
        cy="65"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform="rotate(-90 65 65)"
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      <text x="65" y="64" textAnchor="middle" fontSize="32" fontWeight="800" fill="#0f172a">{score}</text>
      <text x="65" y="84" textAnchor="middle" fontSize="12" fill="#94a3b8">/ 100</text>
    </svg>
  );
};

Gauge.propTypes = { score: PropTypes.number, color: PropTypes.string };

const CustomerRelationScore = ({ customerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    customerApi.getCustomerRelationScore(customerId, window.localStorage.getItem('accessToken'))
      .then((res) => { if (active) setData(res || null); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [customerId]);

  const score = data ? data.score : 0;
  const color = scoreColor(score);
  const components = (data && data.components) || [];
  const flags = (data && data.flags) || [];

  return (
    <Grid item xs={12}>
      <motion.div whileHover={{ y: -2 }}>
        <Card className="glass-card" sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
          <CardHeader
            title="Score relation client"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 900 }}
            avatar={<HandshakeRounded sx={{ color: '#6366f1' }} />}
          />
          <Divider />
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <Skeleton variant="circular" width={130} height={130} />
                <Box sx={{ flexGrow: 1 }}>
                  {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="text" height={26} />)}
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Gauge score={score} color={color} />
                    <Box
                      sx={{
                        mt: 0.5, px: 1.5, py: 0.4, borderRadius: '999px',
                        backgroundColor: `${color}1a`, color, fontWeight: 700, fontSize: '0.82rem'
                      }}
                    >
                      {data ? data.label : '—'}
                    </Box>
                    {data && data.capped && (
                      <Typography sx={{ mt: 0.5, fontSize: '0.68rem', color: '#94a3b8', textAlign: 'center' }}>
                        plafonné (statut perdu)
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ flexGrow: 1, minWidth: 220 }}>
                    {components.map((comp) => {
                      const pct = comp.max > 0 ? Math.round((comp.value / comp.max) * 100) : 0;
                      return (
                        <Box key={comp.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.9 }}>
                          <Typography sx={{ width: 78, flexShrink: 0, fontSize: '0.8rem', color: '#475569' }}>{comp.label}</Typography>
                          <Box sx={{ flexGrow: 1, height: 10, borderRadius: '5px', backgroundColor: 'rgba(148,163,184,0.18)', overflow: 'hidden' }}>
                            <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: '5px', background: 'linear-gradient(90deg, #818cf8, #6366f1)', transition: 'width 0.7s ease' }} />
                          </Box>
                          <Typography sx={{ width: 48, flexShrink: 0, textAlign: 'right', fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
                            {comp.value}<span style={{ color: '#94a3b8', fontWeight: 500 }}>/{comp.max}</span>
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {flags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {flags.map((f) => {
                      const st = flagStyle(f.key);
                      return (
                        <Box
                          key={f.key}
                          sx={{
                            display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.5, borderRadius: '999px',
                            backgroundColor: st.bg, color: st.fg, border: `1px solid ${st.bd}`,
                            fontSize: '0.76rem', fontWeight: 600
                          }}
                        >
                          {f.label}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Grid>
  );
};

CustomerRelationScore.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default CustomerRelationScore;
